import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Table,
	Divider,
	notification,
	Spin,
	Badge,
	Tooltip,
	Select,
	Modal,
} from "antd";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiPencilDuotone,
	PiShoppingCartDuotone,
	PiCurrencyDollarDuotone,
	PiCalendarDuotone,
	PiUserDuotone,
	PiHouseDuotone,
	PiPackageDuotone,
	PiReceiptDuotone,
	PiInfoDuotone,
	PiCheckCircleDuotone,
	PiFileDuotone,
	PiCloudDuotone,
	PiGlobeDuotone,
} from "react-icons/pi";
import {
	requestGetOrderById,
	requestUpdateOrderStatus,
	requestGetInvoiceList,
	requestCreateInvoiceFromOrder,
} from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { Option } = Select;

function OrderViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [updateStatusLoading, setUpdateStatusLoading] = useState(false);
	const [invoicesLoading, setInvoicesLoading] = useState(false);
	const [createInvoiceLoading, setCreateInvoiceLoading] = useState(false);
	const [order, setOrder] = useState(null);
	const [invoices, setInvoices] = useState([]);
	const [statusModalVisible, setStatusModalVisible] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState("");

	useEffect(() => {
		document.title = "Cloudiva Console | Order Details";
		fetchOrderDetails();
	}, [id]);

	useEffect(() => {
		if (order) {
			fetchInvoices();
		}
	}, [order]);

	const fetchOrderDetails = () => {
		setLoading(true);
		requestGetOrderById(id)
			.then((response) => {
				const orderData = response.data.data || response.data;
				setOrder(orderData);
			})
			.catch((error) => {
				console.error("Error fetching order details:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch order details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchInvoices = () => {
		setInvoicesLoading(true);
		requestGetInvoiceList({
			filter: `order:${id}`,
			limit: 100,
		})
			.then((response) => {
				const invoiceData = response.data.data || response.data;

				setInvoices(invoiceData);
			})
			.catch((error) => {
				console.error("Error fetching invoices:", error);
			})
			.finally(() => {
				setInvoicesLoading(false);
			});
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "CONFIRMED":
				return "blue";
			case "COMPLETED":
				return "green";
			case "CANCELLED":
				return "red";
			case "FAILED":
				return "red";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "CONFIRMED":
				return "Confirmed";
			case "COMPLETED":
				return "Completed";
			case "CANCELLED":
				return "Cancelled";
			case "FAILED":
				return "Failed";
			default:
				return status;
		}
	};

	const statusOptions = [
		{ value: "PENDING", label: "Pending", color: "orange" },
		{ value: "CONFIRMED", label: "Confirmed", color: "blue" },
		{ value: "COMPLETED", label: "Completed", color: "green" },
		{ value: "CANCELLED", label: "Cancelled", color: "red" },
		{ value: "FAILED", label: "Failed", color: "red" },
	];

	const handleStatusUpdate = () => {
		setStatusModalVisible(true);
		setSelectedStatus(order?.status || "");
	};

	const handleStatusModalCancel = () => {
		setStatusModalVisible(false);
		setSelectedStatus("");
	};

	const handleStatusModalOk = () => {
		if (!selectedStatus || selectedStatus === order?.status) {
			setStatusModalVisible(false);
			return;
		}

		setUpdateStatusLoading(true);
		requestUpdateOrderStatus(id, { status: selectedStatus })
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Order status updated successfully",
				});
				// Update the local order state
				setOrder((prev) => ({
					...prev,
					status: selectedStatus,
				}));
				setStatusModalVisible(false);
			})
			.catch((error) => {
				console.error("Error updating order status:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to update order status",
				});
			})
			.finally(() => {
				setUpdateStatusLoading(false);
			});
	};

	const handleCreateInvoice = () => {
		setCreateInvoiceLoading(true);
		requestCreateInvoiceFromOrder({ orderId: id })
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Invoice created successfully",
				});
				// Refresh invoices list
				fetchInvoices();
			})
			.catch((error) => {
				console.error("Error creating invoice:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to create invoice",
				});
			})
			.finally(() => {
				setCreateInvoiceLoading(false);
			});
	};

	const getInvoiceStatusColor = (status) => {
		switch (status) {
			case "DRAFT":
				return "default";
			case "PENDING":
				return "orange";
			case "PAID":
				return "green";
			case "OVERDUE":
				return "red";
			case "CANCELLED":
				return "red";
			default:
				return "default";
		}
	};

	const getInvoiceStatusText = (status) => {
		switch (status) {
			case "DRAFT":
				return "Draft";
			case "PENDING":
				return "Pending";
			case "PAID":
				return "Paid";
			case "OVERDUE":
				return "Overdue";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const itemColumns = [
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 100,
			render: (type, item) => (
				<Tag color={type === "DOMAIN" ? "green" : "blue"}>
					{type === "DOMAIN" ? "Domain" : "Instance"}
				</Tag>
			),
		},
		{
			title: "Item Details",
			key: "itemDetails",
			render: (_, item) => {
				if (item.type === "DOMAIN") {
					// Domain item
					return (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
							<div>
								<div style={{ fontWeight: 500 }}>Domain {item.domain?.name || "N/A"}</div>
								<div style={{ fontSize: "12px", color: "#666" }}>
									{item.domain?.extension?.name} ({item.domain?.extension?.code})
									{/* {item.domain?._id && ` • ID: ${item.domain._id}`} */}
								</div>
							</div>
						</div>
					);
				}
				// Instance item
				return (
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
						<div>
							<div style={{ fontWeight: 500 }}>{item.instance?.service?.name || "N/A"}</div>
							<div style={{ fontSize: "12px", color: "#666" }}>
								{item.instance?.plan?.name} ({item.instance?.plan?.code})
								{item.instance?.region?.name && ` • ${item.instance.region.name}`}
								{/* {item.instance?._id && ` • Instance: ${item.instance._id}`} */}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			title: "Subtotal",
			dataIndex: "unitSubtotal",
			key: "unitSubtotal",
			width: 120,
			align: "center",
			render: (value) => {
				return <div style={{ textAlign: "center" }}>{formatCurrency(value, order?.currency)}</div>;
			},
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			align: "center",
			render: (quantity, item) => (
				<Space size={2}>
					<div style={{ fontWeight: 500 }}>{quantity}</div>
					{item.type === "DOMAIN" ? (
						<div style={{ fontSize: "12px", color: "#666" }}>year{quantity > 1 ? "s" : ""}</div>
					) : (
						item.instance?.plan?.price?.interval && (
							<div style={{ fontSize: "12px", color: "#666" }}>
								{item.instance.plan.price.interval?.toLowerCase()}
							</div>
						)
					)}
				</Space>
			),
		},
		{
			title: "Total",
			dataIndex: "lineTotal",
			key: "lineTotal",
			width: 120,
			align: "center",
			render: (value) => {
				return <div style={{ textAlign: "center" }}>{formatCurrency(value, order?.currency)}</div>;
			},
		},
		{
			title: "Status",
			key: "status",
			width: 120,
			align: "center",
			render: (_, item) => {
				if (item.type === "DOMAIN") {
					// Domain items - show domain status or generic active status
					if (item.domain._id) {
						return (
							<Link to={`/domains/${item.domain._id}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 4,
									}}>
									<PiGlobeDuotone size={16} style={{ marginRight: 4, color: "#52c41a" }} />
									<Tag color="green" size="small">
										Registered
									</Tag>
								</div>
							</Link>
						);
					} else {
						return (
							<div style={{ textAlign: "center" }}>
								<Tag color="red" size="small">
									Inactive
								</Tag>
							</div>
						);
					}
				}
				// Instance items - show provisioning status
				return (
					<div style={{ textAlign: "center" }}>
						{item.instance?._id ? (
							<Link to={`/instances/${item.instance._id}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 4,
									}}>
									<PiCloudDuotone size={16} style={{ marginRight: 4, color: "#52c41a" }} />
									<Tag color="green" size="small">
										Provisioned
									</Tag>
								</div>
							</Link>
						) : (
							<div style={{ color: "#999" }}>
								<PiCloudDuotone size={16} style={{ marginBottom: 4, opacity: 0.5 }} />
								<div style={{ fontSize: "12px" }}>Not provisioned</div>
							</div>
						)}
					</div>
				);
			},
		},
	];

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!order) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Text>Order not found</Text>
				</div>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/orders")} />
						<Title level={4} style={{ margin: 0 }}>
							Order Details
						</Title>
						<Tag color={getStatusColor(order.status)}>{getStatusText(order.status)}</Tag>
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Row gutter={24}>
					{/* Order Information */}
					<Col span={16}>
						<Card
							title={
								<Space>
									<PiShoppingCartDuotone size={20} />
									<span>Order Information</span>
								</Space>
							}
							extra={
								<Space>
									<Button
										icon={<PiCheckCircleDuotone />}
										onClick={handleStatusUpdate}
										loading={updateStatusLoading}>
										Update Status
									</Button>
									<Button
										type="primary"
										icon={<PiPencilDuotone />}
										onClick={() => navigate(`/orders/${order._id}/edit`)}>
										Edit Order
									</Button>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Descriptions column={2} bordered>
								<Descriptions.Item label="Order Number" span={1}>
									<Text strong>{order.number}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Status" span={1}>
									<Tag color={getStatusColor(order.status)}>{getStatusText(order.status)}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Currency" span={1}>
									<Tag color="blue">{order.currency}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Total Items" span={1}>
									<Badge count={order.items?.length || 0} showZero />
								</Descriptions.Item>
								<Descriptions.Item label="Issued Date" span={1}>
									<Space>
										<PiCalendarDuotone />
										{dayjs(order.issuedAt).format("DD MMMM YYYY, HH:mm")}
									</Space>
								</Descriptions.Item>
								<Descriptions.Item label="Expiry Date" span={1}>
									<Space>
										<PiCalendarDuotone />
										{dayjs(order.expiredAt).format("DD MMMM YYYY, HH:mm")}
									</Space>
								</Descriptions.Item>
								{order.team && (
									<Descriptions.Item label="Team" span={2}>
										<Space>
											<PiUserDuotone />
											<Text>{order.team.name}</Text>
											{order.team.currency && <Tag color="green">{order.team.currency}</Tag>}
										</Space>
									</Descriptions.Item>
								)}
							</Descriptions>
						</Card>

						{/* Order Items */}
						<Card
							title={
								<Space>
									<PiPackageDuotone size={20} />
									<span>Order Items</span>
									<Badge count={order.items?.length || 0} />
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Table
								columns={itemColumns}
								dataSource={order.items}
								pagination={false}
								rowKey={(item, index) => `${order._id}-${index}`}
								size="small"
								summary={(pageData) => {
									const subtotal = order.subtotal;
									const tax = order.taxTotal || 0;
									const discount = order.discountTotal || 0;
									const total = order.total || 0;

									return (
										<Table.Summary>
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={5}>
													<Text strong>Subtotal</Text>
												</Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right">
													<Text strong>{formatCurrency(subtotal, order.currency)}</Text>
												</Table.Summary.Cell>
											</Table.Summary.Row>
											{discount > 0 && (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={5}>
														<Text type="success">Discount</Text>
													</Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right">
														<Text type="success">-{formatCurrency(discount, order.currency)}</Text>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)}
											{tax > 0 && (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={5}>
														<Text>Tax</Text>
													</Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right">
														<Text>{formatCurrency(tax, order.currency)}</Text>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)}
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={5}>
													<Text strong style={{ fontSize: "16px" }}>
														Grand Total
													</Text>
												</Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right">
													<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
														{formatCurrency(total, order.currency)}
													</Text>
												</Table.Summary.Cell>
											</Table.Summary.Row>
										</Table.Summary>
									);
								}}
							/>
						</Card>

						{/* Invoices */}
						<Card
							title={
								<Space>
									<PiReceiptDuotone size={20} />
									<span>Related Invoices</span>
									<Badge count={invoices.length} showZero />
								</Space>
							}
							extra={
								<Button
									type="primary"
									icon={<PiFileDuotone />}
									onClick={handleCreateInvoice}
									loading={createInvoiceLoading}>
									Create Invoice
								</Button>
							}
							style={{ marginBottom: 24 }}>
							{invoicesLoading ? (
								<div style={{ textAlign: "center", padding: "20px 0" }}>
									<Spin />
								</div>
							) : invoices.length === 0 ? (
								<div style={{ textAlign: "center", padding: "20px 0", color: "#666" }}>
									<PiReceiptDuotone size={48} style={{ marginBottom: 8, opacity: 0.3 }} />
									<div>No invoices created yet</div>
									<div style={{ fontSize: "12px" }}>
										Click "Create Invoice" to generate an invoice from this order
									</div>
								</div>
							) : (
								<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
									{invoices.map((invoice) => (
										<div
											key={invoice._id}
											style={{
												border: "1px solid #f0f0f0",
												borderRadius: "6px",
												padding: "12px",
												backgroundColor: "#fafafa",
											}}>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													marginBottom: 8,
												}}>
												<div>
													<Text strong>{invoice.number}</Text>
													<Tag
														color={getInvoiceStatusColor(invoice.status)}
														style={{ marginLeft: 8 }}>
														{getInvoiceStatusText(invoice.status)}
													</Tag>
												</div>
												<Button
													type="link"
													size="small"
													onClick={() => navigate(`/invoices/${invoice._id}`)}>
													View
												</Button>
											</div>
											<div style={{ fontSize: "12px", color: "#666" }}>
												<div>
													<strong>Total:</strong>{" "}
													{formatCurrency(invoice.total || 0, invoice.currency)}
												</div>
												<div>
													<strong>Issued:</strong> {dayjs(invoice.issuedAt).format("DD MMM YYYY")}
												</div>
												{invoice.expiredAt && (
													<div>
														<strong>Due:</strong> {dayjs(invoice.expiredAt).format("DD MMM YYYY")}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</Card>
					</Col>

					{/* Billing & Additional Info */}
					<Col span={8}>
						{/* Billing Address */}
						<Card
							title={
								<Space>
									<PiUserDuotone size={20} />
									<span>Billing Address</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							{order.billingAddress ? (
								<div>
									<div style={{ marginBottom: 8 }}>
										<Text strong>{order.billingAddress.name}</Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Text>{order.billingAddress.email}</Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Text>{order.billingAddress.phone}</Text>
									</div>
									{order.billingAddress.address && (
										<>
											<Divider style={{ margin: "8px 0" }} />
											<div style={{ fontSize: "12px", color: "#666" }}>
												<div>{order.billingAddress.address}</div>
												<div>
													{order.billingAddress.city}, {order.billingAddress.state}{" "}
													{order.billingAddress.postalCode}
												</div>
												<div>{order.billingAddress.country}</div>
											</div>
										</>
									)}
								</div>
							) : (
								<Text type="secondary">No billing address available</Text>
							)}
						</Card>

						{/* Financial Summary */}
						<Card
							title={
								<Space>
									<PiCurrencyDollarDuotone size={20} />
									<span>Financial Summary</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text>Subtotal:</Text>
									<Text>{formatCurrency(order.subtotal || 0, order.currency)}</Text>
								</div>
								{order.discountTotal > 0 && (
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<Text type="success">Discount:</Text>
										<Text type="success">
											-{formatCurrency(order.discountTotal, order.currency)}
										</Text>
									</div>
								)}
								{order.taxTotal > 0 && (
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<Text>Tax:</Text>
										<Text>{formatCurrency(order.taxTotal, order.currency)}</Text>
									</div>
								)}
								<Divider style={{ margin: "8px 0" }} />
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text strong style={{ fontSize: "16px" }}>
										Grand Total:
									</Text>
									<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
										{formatCurrency(order.total || 0, order.currency)}
									</Text>
								</div>
							</div>
						</Card>

						{/* Metadata */}
						{order.metadata && Object.keys(order.metadata).length > 0 && (
							<Card
								title={
									<Space>
										<PiInfoDuotone size={20} />
										<span>Additional Information</span>
									</Space>
								}>
								<div style={{ backgroundColor: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
									<pre style={{ margin: 0, fontSize: "12px" }}>
										{JSON.stringify(order.metadata, null, 2)}
									</pre>
								</div>
							</Card>
						)}
					</Col>
				</Row>
			</div>

			{/* Status Update Modal */}
			<Modal
				title="Update Order Status"
				open={statusModalVisible}
				onOk={handleStatusModalOk}
				onCancel={handleStatusModalCancel}
				confirmLoading={updateStatusLoading}
				okText="Update Status"
				cancelText="Cancel">
				<div style={{ marginBottom: 16 }}>
					<Text>Current Status: </Text>
					<Tag color={getStatusColor(order?.status)}>{getStatusText(order?.status)}</Tag>
				</div>
				<div>
					<Text>Select New Status:</Text>
					<Select
						value={selectedStatus}
						onChange={setSelectedStatus}
						style={{ width: "100%", marginTop: 8 }}
						placeholder="Select status">
						{statusOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								<Tag color={option.color} style={{ marginRight: 8 }}>
									{option.label}
								</Tag>
							</Option>
						))}
					</Select>
				</div>
			</Modal>
		</div>
	);
}

export default OrderViewPage;
